import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plane, MapPin, Calendar, Users, DollarSign, FileText, Search, Settings, 
  BarChart3, CreditCard, Building, Target, MessageSquare, Plus, Home as HomeIcon,
  ChevronRight, Zap
} from 'lucide-react';
import api from '../services/api';
import { useAuthContext } from '../contexts/AuthContext';
import DashboardMetrics from './dashboard/DashboardMetrics';

interface PageTile {
  id: number;
  page_name: string;
  page_path: string;
  page_category: string;
  description: string;
  icon: string;
  can_view: boolean;
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { userRole, token } = useAuthContext();
  const [accessiblePages, setAccessiblePages] = useState<PageTile[]>([]);
  const [loading, setLoading] = useState(true);

  // Icon mapping
  const getIcon = (iconName: string) => {
    const icons: { [key: string]: React.ReactNode } = {
      'Home': <HomeIcon className="w-6 h-6" />,
      'FileText': <FileText className="w-6 h-6" />,
      'DollarSign': <DollarSign className="w-6 h-6" />,
      'Search': <Search className="w-6 h-6" />,
      'Calendar': <Calendar className="w-6 h-6" />,
      'Settings': <Settings className="w-6 h-6" />,
      'BarChart3': <BarChart3 className="w-6 h-6" />,
      'CreditCard': <CreditCard className="w-6 h-6" />,
      'Building': <Building className="w-6 h-6" />,
      'Target': <Target className="w-6 h-6" />,
      'MessageSquare': <MessageSquare className="w-6 h-6" />,
      'Plus': <Plus className="w-6 h-6" />,
      'Users': <Users className="w-6 h-6" />,
      'Plane': <Plane className="w-6 h-6" />,
      'MapPin': <MapPin className="w-6 h-6" />
    };
    return icons[iconName] || <FileText className="w-6 h-6" />;
  };

  // Category colors
  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'Main': 'from-blue-500 to-blue-600',
      'Finance': 'from-green-500 to-green-600',
      'Operations': 'from-purple-500 to-purple-600',
      'Analytics': 'from-orange-500 to-orange-600',
      'Admin': 'from-red-500 to-red-600',
      'Work Assignment': 'from-indigo-500 to-indigo-600',
      'Reports': 'from-teal-500 to-teal-600'
    };
    return colors[category] || 'from-gray-500 to-gray-600';
  };

  useEffect(() => {
    // Only fetch pages when we have a token (authentication is complete)
    if (token) {
      fetchAccessiblePages();
    }
  }, [token]);

  const fetchAccessiblePages = async () => {
    try {
      const response = await api.get('/role-pages/menu');
      if (response.data.success) {
        const data = response.data.data;
        
        // Handle categorized structure
        if (data.categories) {
          const allPages: PageTile[] = [];
          data.categories.forEach((category: any) => {
            category.items.forEach((item: any) => {
              // Filter out Dashboard/Home pages
              if (item.label === 'Dashboard' || item.label === 'Home' || item.path === '/home' || item.path === '/dashboard') {
                return;
              }
              
              allPages.push({
                id: item.id || Math.random(),
                page_name: item.label,
                page_path: item.path,
                page_category: category.name,
                description: getPageDescription(item.label, item.path),
                icon: item.icon,
                can_view: true
              });
            });
          });
          setAccessiblePages(allPages);
        }
        // Fallback to flat menu items
        else if (data.menu_items) {
          const pages = data.menu_items
            .filter((item: any) => {
              // Filter out Dashboard/Home pages
              return !(item.label === 'Dashboard' || item.label === 'Home' || item.path === '/home' || item.path === '/dashboard');
            })
            .map((item: any) => ({
              id: item.id || Math.random(),
              page_name: item.label,
              page_path: item.path,
              page_category: 'Main',
              description: getPageDescription(item.label, item.path),
              icon: item.icon,
              can_view: true
            }));
          setAccessiblePages(pages);
        }
      }
    } catch (error) {
      console.error('Error fetching accessible pages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get meaningful descriptions for pages
  const getPageDescription = (label: string, path: string) => {
    const descriptions: { [key: string]: string } = {
      'Balify': 'Create and manage travel itineraries',
      'Balify Table': 'View and manage all itineraries in table format',
      'My Assignments': 'View and manage your assigned work tasks',
      'Work Pool': 'Browse and pick up available work assignments',
      'Work Assignment Dashboard': 'Monitor team performance and assignment metrics',
      'Create Enquiry': 'Create new travel enquiries and requests',
      'Accounts V3': 'Manage payments, invoices and financial transactions',
      'Booking Management': 'Handle bookings and reservations',
      'Hotel Management': 'Manage hotel inventory and bookings',
      'Role Management': 'Configure user roles and permissions',
      'Confirmed Trips': 'View and manage confirmed travel bookings',
      'Invoice and Payments': 'Process invoices and payment transactions',
      'Calendar': 'View schedules and important dates',
      'Reports': 'Generate and view business reports',
      'Payments Report': 'Analyze payment trends and data',
      'Profit Loss Report': 'View financial performance metrics',
      'Invoice Report': 'Track invoice status and history',
      'Payment Received Report': 'Monitor received payments'
    };
    
    return descriptions[label] || `Manage ${label.toLowerCase()} operations`;
  };

  const handleTileClick = (path: string) => {
    navigate(path);
  };

  // Group pages by category and prioritize Work Assignment
  const pagesByCategory = accessiblePages.reduce((acc, page) => {
    if (!acc[page.page_category]) {
      acc[page.page_category] = [];
    }
    acc[page.page_category].push(page);
    return acc;
  }, {} as { [key: string]: PageTile[] });

  // Sort categories to show Work Assignment first
  const sortedCategories = Object.entries(pagesByCategory).sort(([categoryA], [categoryB]) => {
    if (categoryA === 'Work Assignment') return -1;
    if (categoryB === 'Work Assignment') return 1;
    return categoryA.localeCompare(categoryB);
  });

  if (loading) {
    return (
      <div className="h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col overflow-y-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Welcome to Wynder Holidays
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto mb-2">
            Your gateway to exceptional travel experiences
          </p>
          <p className="text-sm text-gray-500">
            Role: <span className="font-medium text-blue-600">{userRole}</span>
          </p>
        </div>

        {/* Dashboard Metrics */}
        <DashboardMetrics />
      </div>
    </div>
  );
};

export default HomePage;